/*
 * Copyright 2024 the original author or authors.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { BooleanVectorComponentValue, ChangedNumberVectorComponentValue, ComponentCategory, CvssVector, MultiScoreResult, NumberVectorComponentValue, VectorComponent, VectorComponentValue } from "./CvssVector";

const AC_VALUES = {
    X: {
        shortName: 'X',
        value: 1.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    L: {
        shortName: 'L',
        value: 0.77,
        name: 'Low',
        jsonSchemaName: 'LOW',
        description: 'Specialized access conditions or extenuating circumstances do not exist. An attacker can expect repeatable success when attacking the vulnerable component.'
    },
    H: {
        shortName: 'H',
        value: 0.44,
        name: 'High',
        jsonSchemaName: 'HIGH',
        description: 'A successful attack depends on conditions beyond the attacker\'s control. That is, a successful attack cannot be accomplished at will, but requires the attacker to invest in some measurable amount of effort in preparation or execution against the vulnerable component before a successful attack can be expected.'
    }
};


const PR_VALUES = {
    X: {
        shortName: 'X',
        value: 1.0,
        changedValue: 1.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    N: {
        shortName: 'N',
        value: 0.85,
        changedValue: 0.85,
        name: 'None',
        jsonSchemaName: 'NONE',
        description: 'The attacker is unauthorized prior to attack, and therefore does not require any access to settings or files of the vulnerable system to carry out an attack.'
    },
    L: {
        shortName: 'L',
        value: 0.62,
        changedValue: 0.68,
        name: 'Low',
        jsonSchemaName: 'LOW',
        description: 'The attacker requires privileges that provide basic user capabilities that could normally affect only settings and files owned by a user. Alternatively, an attacker with Low privileges has the ability to access only non-sensitive resources.'
    },
    H: {
        shortName: 'H',
        value: 0.27,
        changedValue: 0.5,
        name: 'High',
        jsonSchemaName: 'HIGH',
        description: 'The attacker requires privileges that provide significant (e.g., administrative) control over the vulnerable component allowing access to component-wide settings and files.'
    }
}


const BASE_CATEGORY: ComponentCategory = {
    name: 'base',
    description: 'This metric reflects the qualities of a vulnerability that are constant over time and across user environments.'
};

const AV_VALUES = {
    X: {
        shortName: 'X',
        value: 1.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    N: {
        shortName: 'N',
        value: 0.85,
        name: 'Network',
        abbreviatedName: 'Netw.',
        jsonSchemaName: 'NETWORK',
        description: 'The vulnerable component is bound to the network stack and the set of possible attackers extends beyond the other options listed below, up to and including the entire Internet.'
    },
    A: {
        shortName: 'A',
        value: 0.62,
        name: 'Adjacent Network',
        abbreviatedName: 'Adj. Network',
        jsonSchemaName: 'ADJACENT_NETWORK',
        description: 'The vulnerable component is bound to the network stack, but the attack is limited at the protocol level to a logically adjacent topology.'
    },
    L: {
        shortName: 'L',
        value: 0.55,
        name: 'Local',
        jsonSchemaName: 'LOCAL',
        description: 'The vulnerable component is not bound to the network stack and the attacker\'s path is via read/write/execute capabilities.'
    },
    P: {
        shortName: 'P',
        value: 0.2,
        name: 'Physical',
        abbreviatedName: 'Phys.',
        jsonSchemaName: 'PHYSICAL',
        description: 'The attack requires the attacker to physically touch or manipulate the vulnerable component.'
    }
}

const CONFIDENTIALITY_IMPACT_VALUES = {
    X: {
        shortName: 'X',
        value: 0.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    N: {
        shortName: 'N',
        value: 0.0,
        name: 'None',
        jsonSchemaName: 'NONE',
        description: 'There is no loss of confidentiality within the impacted component.'
    },
    L: {
        shortName: 'L',
        value: 0.22,
        name: 'Low',
        jsonSchemaName: 'LOW',
        description: 'There is some loss of confidentiality. Access to some restricted information is obtained, but the attacker does not have control over what information is obtained, or the amount or kind of loss is limited. The information disclosure does not cause a direct, serious loss to the impacted component.'
    },
    H: {
        shortName: 'H',
        value: 0.56,
        name: 'High',
        jsonSchemaName: 'HIGH',
        description: 'There is a total loss of confidentiality, resulting in all resources within the impacted component being divulged to the attacker. Alternatively, access to only some restricted information is obtained, but the disclosed information presents a direct, serious impact. For example, an attacker steals the administrator\'s password, or private encryption keys of a web server.'
    }
};

const INTEGRITY_IMPACT_VALUES = {
    X: {
        shortName: 'X',
        value: 0.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    N: {
        shortName: 'N',
        value: 0.0,
        name: 'None',
        jsonSchemaName: 'NONE',
        description: 'There is no loss of integrity within the impacted component.'
    },
    L: {
        shortName: 'L',
        value: 0.22,
        name: 'Low',
        jsonSchemaName: 'LOW',
        description: 'Modification of data is possible, but the attacker does not have control over the consequence of a modification, or the amount of modification is limited. The data modification does not have a direct, serious impact on the impacted component.'
    },
    H: {
        shortName: 'H',
        value: 0.56,
        name: 'High',
        jsonSchemaName: 'HIGH',
        description: 'There is a total loss of integrity, or a complete loss of protection. For example, the attacker is able to modify any/all files protected by the impacted component. Alternatively, only some files can be modified, but malicious modification would present a direct, serious consequence to the impacted component.'
    }
};


const AVAILABILITY_IMPACT_VALUES = {
    X: {
        shortName: 'X',
        value: 0.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    N: {
        shortName: 'N',
        value: 0.0,
        name: 'None',
        jsonSchemaName: 'NONE',
        description: 'There is no impact to availability within the impacted component.'
    },
    L: {
        shortName: 'L',
        value: 0.22,
        name: 'Low',
        jsonSchemaName: 'LOW',
        description: 'Performance is reduced or there are interruptions in resource availability. Even if repeated exploitation of the vulnerability is possible, the attacker does not have the ability to completely deny service to legitimate users. The resources in the impacted component are either partially available all of the time, or fully available only some of the time, but overall there is no direct, serious consequence to the impacted component.'
    },
    H: {
        shortName: 'H',
        value: 0.56,
        name: 'High',
        jsonSchemaName: 'HIGH',
        description: 'There is a total loss of availability, resulting in the attacker being able to fully deny access to resources in the impacted component; this loss is either sustained (while the attacker continues to deliver the attack) or persistent (the condition persists even after the attack has completed). Alternatively, the attacker has the ability to deny some availability, but the loss of availability presents a direct, serious consequence to the impacted component (e.g., the attacker cannot disrupt existing connections, but can prevent new connections; the attacker can repeatedly exploit a vulnerability that, in each instance of a successful attack, leaks a only small amount of memory, but after repeated exploitation causes a service to become completely unavailable).'
    }
};

const CONFIDENTIALITY_REQUIREMENT_VALUES = {
    X: {
        shortName: 'X',
        value: 1.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Assigning this value indicates there is insufficient information to choose one of the other values, and has no impact on the overall Environmental Score, i.e., it has the same effect on scoring as assigning Medium.'
    },
    L: {
        shortName: 'L',
        value: 0.5,
        name: 'Low',
        jsonSchemaName: 'LOW',
        description: 'Loss of Confidentiality is likely to have only a limited adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).'
    },
    M: {
        shortName: 'M',
        value: 1.0,
        name: 'Medium',
        jsonSchemaName: 'MEDIUM',
        description: 'Loss of Confidentiality is likely to have a serious adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).'
    },
    H: {
        shortName: 'H',
        value: 1.5,
        name: 'High',
        jsonSchemaName: 'HIGH',
        description: 'Loss of Confidentiality is likely to have a catastrophic adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).'
    }
};

const INTEGRITY_REQUIREMENT_VALUES = {
    X: {
        shortName: 'X',
        value: 1.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Assigning this value indicates there is insufficient information to choose one of the other values, and has no impact on the overall Environmental Score, i.e., it has the same effect on scoring as assigning Medium.'
    },
    L: {
        shortName: 'L',
        value: 0.5,
        name: 'Low',
        jsonSchemaName: 'LOW',
        description: 'Loss of Integrity is likely to have only a limited adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).'
    },
    M: {
        shortName: 'M',
        value: 1.0,
        name: 'Medium',
        jsonSchemaName: 'MEDIUM',
        description: 'Loss of Integrity is likely to have a serious adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).'
    },
    H: {
        shortName: 'H',
        value: 1.5,
        name: 'High',
        jsonSchemaName: 'HIGH',
        description: 'Loss of Integrity is likely to have a catastrophic adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).'
    }
};

const AVAILABILITY_REQUIREMENT_VALUES = {
    X: {
        shortName: 'X',
        value: 1.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Assigning this value indicates there is insufficient information to choose one of the other values, and has no impact on the overall Environmental Score, i.e., it has the same effect on scoring as assigning Medium.'
    },
    L: {
        shortName: 'L',
        value: 0.5,
        name: 'Low',
        jsonSchemaName: 'LOW',
        description: 'Loss of Availability is likely to have only a limited adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).'
    },
    M: {
        shortName: 'M',
        value: 1.0,
        name: 'Medium',
        jsonSchemaName: 'MEDIUM',
        description: 'Loss of Availability is likely to have a serious adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).'
    },
    H: {
        shortName: 'H',
        value: 1.5,
        name: 'High',
        jsonSchemaName: 'HIGH',
        description: 'Loss of Availability is likely to have a catastrophic adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).'
    }
};

/*
const TEMPLATE_CIA_REQUIREMENT_MODIFIED_VALUES = {
    X: {
        shortName: 'X',
        value: 1.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    L: {
        shortName: 'L',
        value: 0.5,
        name: 'Low',
        jsonSchemaName: 'LOW',
        description: 'There is no impact to the integrity of the system.'
    },
    M: {
        shortName: 'M',
        value: 1.0,
        name: 'Medium',
        jsonSchemaName: 'MEDIUM',
        description: 'There is a partial compromise of system integrity.'
    },
    H: {
        shortName: 'H',
        value: 1.5,
        name: 'High',
        jsonSchemaName: 'HIGH',
        description: 'There is a total compromise of system integrity.'
    }
};
*/


const CONFIDENTIALITY_IMPACT = [
    CONFIDENTIALITY_IMPACT_VALUES.X,
    CONFIDENTIALITY_IMPACT_VALUES.N,
    CONFIDENTIALITY_IMPACT_VALUES.L,
    CONFIDENTIALITY_IMPACT_VALUES.H
] as NumberVectorComponentValue[];



const INTEGRITY_IMPACT = [
    INTEGRITY_IMPACT_VALUES.X,
    INTEGRITY_IMPACT_VALUES.N,
    INTEGRITY_IMPACT_VALUES.L,
    INTEGRITY_IMPACT_VALUES.H
] as NumberVectorComponentValue[];


const AVAILABILITY_IMPACT = [
    AVAILABILITY_IMPACT_VALUES.X,
    AVAILABILITY_IMPACT_VALUES.N,
    AVAILABILITY_IMPACT_VALUES.L,
    AVAILABILITY_IMPACT_VALUES.H
] as NumberVectorComponentValue[];


const CONFIDENTIALITY_REQUIREMENT = [
    CONFIDENTIALITY_REQUIREMENT_VALUES.X,
    CONFIDENTIALITY_REQUIREMENT_VALUES.L,
    CONFIDENTIALITY_REQUIREMENT_VALUES.M,
    CONFIDENTIALITY_REQUIREMENT_VALUES.H
] as NumberVectorComponentValue[];

const INTEGRITY_REQUIREMENT = [
    INTEGRITY_REQUIREMENT_VALUES.X,
    INTEGRITY_REQUIREMENT_VALUES.L,
    INTEGRITY_REQUIREMENT_VALUES.M,
    INTEGRITY_REQUIREMENT_VALUES.H
] as NumberVectorComponentValue[];


const AVAILABILITY_REQUIREMENT = [
    AVAILABILITY_REQUIREMENT_VALUES.X,
    AVAILABILITY_REQUIREMENT_VALUES.L,
    AVAILABILITY_REQUIREMENT_VALUES.M,
    AVAILABILITY_REQUIREMENT_VALUES.H
] as NumberVectorComponentValue[];

/*
const TEMPLATE_CIA_REQUIREMENT_MODIFIED = [
    TEMPLATE_CIA_REQUIREMENT_MODIFIED_VALUES.X,
    TEMPLATE_CIA_REQUIREMENT_MODIFIED_VALUES.L,
    TEMPLATE_CIA_REQUIREMENT_MODIFIED_VALUES.M,
    TEMPLATE_CIA_REQUIREMENT_MODIFIED_VALUES.H
] as NumberVectorComponentValue[];
*/

const AV = {
    name: 'Attack Vector',
    shortName: 'AV',
    subCategory: 'Exploitability Metrics',
    description: 'This metric reflects the context by which vulnerability exploitation is possible. The more remote an attacker can be to attack a host, the greater the vulnerability score.',
    values: [
        AV_VALUES.X,
        AV_VALUES.N,
        AV_VALUES.A,
        AV_VALUES.L,
        AV_VALUES.P
    ] as NumberVectorComponentValue[]
};

const AC = {
    name: 'Attack Complexity',
    shortName: 'AC',
    subCategory: 'Exploitability Metrics',
    description: 'This metric describes the conditions beyond the attacker\'s control that must exist in order to exploit the vulnerability.',
    values: [
        AC_VALUES.X,
        AC_VALUES.L,
        AC_VALUES.H
    ] as NumberVectorComponentValue[]
};



const PR = {
    name: 'Privileges Required',
    shortName: 'PR',
    subCategory: 'Exploitability Metrics',
    description: 'This metric describes the level of privileges an attacker must possess before successfully exploiting the vulnerability.',
    values: [
        PR_VALUES.X,
        PR_VALUES.N,
        PR_VALUES.L,
        PR_VALUES.H
    ] as ChangedNumberVectorComponentValue[]
};

const UI_VALUES = {
    X: {
        shortName: 'X',
        value: 1.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    N: {
        shortName: 'N',
        value: 0.85,
        name: 'None',
        jsonSchemaName: 'NONE',
        description: 'The vulnerable system can be exploited without interaction from any user.'
    },
    R: {
        shortName: 'R',
        value: 0.62,
        name: 'Required',
        abbreviatedName: 'Req.',
        jsonSchemaName: 'REQUIRED',
        description: 'Successful exploitation of this vulnerability requires a user to take some action before the vulnerability can be exploited. For example, a successful exploit may only be possible during the installation of an application by a system administrator.'
    }
}

const UI = {
    name: 'User Interaction',
    shortName: 'UI',
    subCategory: 'Exploitability Metrics',
    description: 'This metric captures the requirement for a user, other than the attacker, to participate in the successful compromise of the vulnerable component.',
    values: [
        UI_VALUES.X,
        UI_VALUES.N,
        UI_VALUES.R
    ] as NumberVectorComponentValue[]
};

const S_VALUES = {
    X: {
        shortName: 'X',
        value: false,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    U: {
        shortName: 'U',
        value: false,
        name: 'Unchanged',
        abbreviatedName: 'Unchang.',
        jsonSchemaName: 'UNCHANGED',
        description: 'An exploited vulnerability can only affect resources managed by the same authority.'
    },
    C: {
        shortName: 'C',
        value: true,
        name: 'Changed',
        jsonSchemaName: 'CHANGED',
        description: 'An exploited vulnerability can affect resources beyond the authorization privileges intended by the vulnerable system\'s design.'
    }
}

const S = {
    name: 'Scope',
    shortName: 'S',
    subCategory: 'Exploitability Metrics',
    description: 'Can an exploit of the vulnerability be accomplished remotely?',
    values: [
        S_VALUES.X,
        S_VALUES.U,
        S_VALUES.C
    ] as BooleanVectorComponentValue[]
};

const C = {
    name: 'Confidentiality Impact',
    shortName: 'C',
    subCategory: 'Impact Metrics',
    description: 'This metric measures the impact to the confidentiality of the information resources managed by a software component due to a successfully exploited vulnerability.',
    values: CONFIDENTIALITY_IMPACT
};

const I = {
    name: 'Integrity Impact',
    shortName: 'I',
    subCategory: 'Impact Metrics',
    description: 'This metric measures the impact to integrity of a successfully exploited vulnerability.',
    values: INTEGRITY_IMPACT
};

const A = {
    name: 'Availability Impact',
    shortName: 'A',
    subCategory: 'Impact Metrics',
    description: 'This metric measures the impact to the availability of the impacted component resulting from a successfully exploited vulnerability.',
    values: AVAILABILITY_IMPACT
};

const BASE_CATEGORY_VALUES: VectorComponent<VectorComponentValue>[] = [
    AV, AC, PR, UI, S, C, I, A
];

const TEMPORAL_CATEGORY: ComponentCategory = {
    name: 'temporal',
    description: 'This metric reflects the current state of exploit techniques or code availability.',
};

const E_VALUES = {
    X: {
        shortName: 'X',
        value: 1.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    U: {
        shortName: 'U',
        value: 0.91,
        name: 'Unproven',
        abbreviatedName: 'Unproven',
        description: 'No exploit code is available, or an exploit is theoretical.'
    },
    P: {
        shortName: 'P',
        value: 0.94,
        name: 'Proof-of-Concept',
        abbreviatedName: 'Proof-of-conc.',
        jsonSchemaName: 'PROOF_OF_CONCEPT',
        description: 'Proof-of-concept exploit code is available, or an attack demonstration is not practical for most systems. The code or technique is not functional in all situations and may require substantial modification by a skilled attacker.'
    },
    F: {
        shortName: 'F',
        value: 0.97,
        name: 'Functional',
        jsonSchemaName: 'FUNCTIONAL',
        description: 'Functional exploit code is available. The code works in most situations where the vulnerability exists.'
    },
    H: {
        shortName: 'H',
        value: 1.0,
        name: 'High',
        jsonSchemaName: 'HIGH',
        description: 'Functional exploit code is available. The code is widespread and automated and works in all situations where the vulnerability exists.'
    }
}

const E = {
    name: 'Exploit Code Maturity',
    shortName: 'E',
    description: 'This metric measures the likelihood of the vulnerability being attacked, and is typically based on the current state of exploit techniques, exploit code availability, or active, successful exploitation of the vulnerability.',
    values: [
        E_VALUES.X,
        E_VALUES.U,
        E_VALUES.P,
        E_VALUES.F,
        E_VALUES.H
    ] as NumberVectorComponentValue[]
};

const RL_VALUES = {
    X: {
        shortName: 'X',
        value: 1.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    O: {
        shortName: 'O',
        value: 0.95,
        name: 'Official Fix',
        abbreviatedName: 'Off. Fix',
        jsonSchemaName: 'OFFICIAL_FIX',
        description: 'A complete vendor solution is available. Either the vendor has issued an official patch, or an upgrade is available.'
    },
    T: {
        shortName: 'T',
        value: 0.96,
        name: 'Temporary Fix',
        abbreviatedName: 'Temp. Fix',
        jsonSchemaName: 'TEMPORARY_FIX',
        description: 'There is an official but temporary fix available. This includes instances where the vendor issues a temporary hotfix, tool, or workaround.'
    },
    W: {
        shortName: 'W',
        value: 0.97,
        name: 'Workaround',
        jsonSchemaName: 'WORKAROUND',
        description: 'There is an unofficial, non-vendor solution available. In some cases, users of the affected technology will create a patch of their own or provide steps to work around or otherwise mitigate the vulnerability.'
    },
    U: {
        shortName: 'U',
        value: 1.0,
        name: 'Unavailable',
        jsonSchemaName: 'UNAVAILABLE',
        description: 'There is either no solution available or it is impossible to apply.'
    }
}

const RL = {
    name: 'Remediation Level',
    shortName: 'RL',
    description: 'This metric describes the remediation level for a vulnerability in an affected resource.',
    values: [
        RL_VALUES.X,
        RL_VALUES.O,
        RL_VALUES.T,
        RL_VALUES.W,
        RL_VALUES.U
    ] as NumberVectorComponentValue[]
};

const RC_VALUES = {
    X: {
        shortName: 'X',
        value: 1.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    U: {
        shortName: 'U',
        value: 0.92,
        name: 'Unknown',
        description: 'Report confidence is unknown.'
    },
    R: {
        shortName: 'R',
        value: 0.96,
        name: 'Reasonable',
        jsonSchemaName: 'REASONABLE',
        description: 'Reasonable confidence exists, or the reported vulnerability is in a component not typically used by a target or not having a large installed base.'
    },
    C: {
        shortName: 'C',
        value: 1.0,
        name: 'Confirmed',
        jsonSchemaName: 'CONFIRMED',
        description: 'Confirmed confidence exists, or the exploit is functional in the environment where the vulnerability exists.'
    }
}

const RC = {
    name: 'Report Confidence',
    shortName: 'RC',
    description: 'This metric describes the level of confidence in the existence of the vulnerability and the credibility of the known technical details.',
    values: [
        RC_VALUES.X,
        RC_VALUES.U,
        RC_VALUES.R,
        RC_VALUES.C
    ] as NumberVectorComponentValue[]
};

const TEMPORAL_CATEGORY_VALUES: VectorComponent<VectorComponentValue>[] = [
    E, RL, RC
];

const ENVIRONMENTAL_CATEGORY: ComponentCategory = {
    name: 'environmental',
    description: 'This metric reflects the characteristics of a vulnerability that are relevant and unique to a particular user\'s environment. This metric can greatly improve the accuracy of a score.'
};

const MAV_VALUES = {
    X: {
        shortName: 'X',
        value: 1.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    N: {
        shortName: 'N',
        value: 0.85,
        name: 'Network',
        abbreviatedName: 'Netw.',
        jsonSchemaName: 'NETWORK',
        description: 'The vulnerable component is bound to the network stack and the set of possible attackers extends beyond the other options listed below, up to and including the entire Internet.'
    },
    A: {
        shortName: 'A',
        value: 0.62,
        name: 'Adjacent Network',
        abbreviatedName: 'Adj. Network',
        jsonSchemaName: 'ADJACENT_NETWORK',
        description: 'The vulnerable component is bound to the network stack, but the attack is limited at the protocol level to a logically adjacent topology.'
    },
    L: {
        shortName: 'L',
        value: 0.55,
        name: 'Local',
        jsonSchemaName: 'LOCAL',
        description: 'The vulnerable component is not bound to the network stack and the attacker\'s path is via read/write/execute capabilities.'
    },
    P: {
        shortName: 'P',
        value: 0.2,
        name: 'Physical',
        abbreviatedName: 'Phys.',
        jsonSchemaName: 'PHYSICAL',
        description: 'The attack requires the attacker to physically touch or manipulate the vulnerable component.'
    }
}

const MAV = {
    name: 'Modified Attack Vector',
    shortName: 'MAV',
    subCategory: 'Exploitability Metrics',
    description: 'This metric reflects the context by which vulnerability exploitation is possible. The more remote an attacker can be to attack a host, the greater the vulnerability score.',
    baseMetricEquivalent: AV,
    values: [
        MAV_VALUES.X,
        MAV_VALUES.N,
        MAV_VALUES.A,
        MAV_VALUES.L,
        MAV_VALUES.P
    ] as NumberVectorComponentValue[]
};

const MAC_VALUES = {
    X: {
        shortName: 'X',
        value: 1.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    L: {
        shortName: 'L',
        value: 0.77,
        name: 'Low',
        jsonSchemaName: 'LOW',
        description: 'Specialized access conditions or extenuating circumstances do not exist. An attacker can expect repeatable success when attacking the vulnerable component.'
    },
    H: {
        shortName: 'H',
        value: 0.44,
        name: 'High',
        jsonSchemaName: 'HIGH',
        description: 'A successful attack depends on conditions beyond the attacker\'s control. That is, a successful attack cannot be accomplished at will, but requires the attacker to invest in some measurable amount of effort in preparation or execution against the vulnerable component before a successful attack can be expected.'
    }
}

const MAC = {
    name: 'Modified Attack Complexity',
    shortName: 'MAC',
    subCategory: 'Exploitability Metrics',
    description: 'This metric describes the conditions beyond the attacker\'s control that must exist in order to exploit the vulnerability.',
    baseMetricEquivalent: AC,
    values: [
        MAC_VALUES.X,
        MAC_VALUES.L,
        MAC_VALUES.H
    ] as NumberVectorComponentValue[]
};

const MPR_VALUES = {
    X: {
        shortName: 'X',
        value: 1.0,
        changedValue: 1.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    N: {
        shortName: 'N',
        value: 0.85,
        changedValue: 0.85,
        name: 'None',
        jsonSchemaName: 'NONE',
        description: 'The attacker is unauthorized prior to attack, and therefore does not require any access to settings or files of the vulnerable system to carry out an attack.'
    },
    L: {
        shortName: 'L',
        value: 0.62,
        changedValue: 0.68,
        name: 'Low',
        jsonSchemaName: 'LOW',
        description: 'The attacker requires privileges that provide basic user capabilities that could normally affect only settings and files owned by a user. Alternatively, an attacker with Low privileges has the ability to access only non-sensitive resources.'
    },
    H: {
        shortName: 'H',
        value: 0.27,
        changedValue: 0.5,
        name: 'High',
        jsonSchemaName: 'HIGH',
        description: 'The attacker requires privileges that provide significant (e.g., administrative) control over the vulnerable component allowing access to component-wide settings and files.'
    }
}

const MPR = {
    name: 'Modified Privileges Required',
    shortName: 'MPR',
    subCategory: 'Exploitability Metrics',
    description: 'This metric describes the level of privileges an attacker must possess before successfully exploiting the vulnerability.',
    baseMetricEquivalent: PR,
    values: [
        MPR_VALUES.X,
        MPR_VALUES.N,
        MPR_VALUES.L,
        MPR_VALUES.H
    ] as ChangedNumberVectorComponentValue[]
};

const MUI_VALUES = {
    X: {
        shortName: 'X',
        value: 1.0,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    N: {
        shortName: 'N',
        value: 0.85,
        name: 'None',
        jsonSchemaName: 'NONE',
        description: 'The vulnerable system can be exploited without interaction from any user.'
    },
    R: {
        shortName: 'R',
        value: 0.62,
        name: 'Required',
        jsonSchemaName: 'REQUIRED',
        description: 'Successful exploitation of this vulnerability requires a user to take some action before the vulnerability can be exploited. For example, a successful exploit may only be possible during the installation of an application by a system administrator.'
    }
}

const MUI = {
    name: 'Modified User Interaction',
    shortName: 'MUI',
    subCategory: 'Exploitability Metrics',
    description: 'This metric captures the requirement for a user, other than the attacker, to participate in the successful compromise of the vulnerable component.',
    baseMetricEquivalent: UI,
    values: [
        MUI_VALUES.X,
        MUI_VALUES.N,
        MUI_VALUES.R
    ] as NumberVectorComponentValue[]
};

const MS_VALUES = {
    X: {
        shortName: 'X',
        value: false,
        name: 'Not Defined',
        abbreviatedName: 'Not Def.',
        jsonSchemaName: 'NOT_DEFINED',
        description: 'Component is not defined.'
    },
    U: {
        shortName: 'U',
        value: false,
        name: 'Unchanged',
        abbreviatedName: 'Unchang.',
        jsonSchemaName: 'UNCHANGED',
        description: 'An exploited vulnerability can only affect resources managed by the same authority.'
    },
    C: {
        shortName: 'C',
        value: true,
        name: 'Changed',
        jsonSchemaName: 'CHANGED',
        description: 'An exploited vulnerability can affect resources beyond the authorization privileges intended by the vulnerable system\'s design.'
    }
}

const MS = {
    name: 'Modified Scope',
    shortName: 'MS',
    subCategory: 'Exploitability Metrics',
    description: 'Can an exploit of the vulnerability be accomplished remotely?',
    baseMetricEquivalent: S,
    values: [
        MS_VALUES.X,
        MS_VALUES.U,
        MS_VALUES.C
    ] as BooleanVectorComponentValue[]
};

const MC = {
    name: 'Confidentiality Impact',
    shortName: 'MC',
    subCategory: 'Modified Impact',
    description: 'This metric measures the impact to the confidentiality of the information resources managed by a software component due to a successfully exploited vulnerability.',
    baseMetricEquivalent: C,
    values: CONFIDENTIALITY_IMPACT
};

const MI = {
    name: 'Integrity Impact',
    shortName: 'MI',
    subCategory: 'Modified Impact',
    description: 'This metric measures the impact to integrity of a successfully exploited vulnerability.',
    baseMetricEquivalent: I,
    values: INTEGRITY_IMPACT
};

const MA = {
    name: 'Availability Impact',
    shortName: 'MA',
    subCategory: 'Modified Impact',
    description: 'This metric measures the impact to the availability of the impacted component resulting from a successfully exploited vulnerability.',
    baseMetricEquivalent: A,
    values: AVAILABILITY_IMPACT
};

const CR = {
    name: 'Confidentiality Requirement',
    shortName: 'CR',
    subCategory: 'Modified Requirement (Impact Subscore) Modifiers',
    description: 'This metric describes the remediation level for a vulnerability in an affected resource.',
    values: CONFIDENTIALITY_REQUIREMENT
};

const IR = {
    name: 'Integrity Requirement',
    shortName: 'IR',
    subCategory: 'Modified Requirement (Impact Subscore) Modifiers',
    description: 'This metric describes the remediation level for a vulnerability in an affected resource.',
    values: INTEGRITY_REQUIREMENT
};

const AR = {
    name: 'Availability Requirement',
    shortName: 'AR',
    subCategory: 'Modified Requirement (Impact Subscore) Modifiers',
    description: 'This metric describes the remediation level for a vulnerability in an affected resource.',
    values: AVAILABILITY_REQUIREMENT
};

const ENVIRONMENTAL_CATEGORY_VALUES: VectorComponent<VectorComponentValue>[] = [
    MAV, MAC, MPR, MUI, MS, MC, MI, MA, CR, IR, AR
];

const REGISTERED_COMPONENTS = new Map<ComponentCategory, VectorComponent<VectorComponentValue>[]>();
REGISTERED_COMPONENTS.set(BASE_CATEGORY, BASE_CATEGORY_VALUES);
REGISTERED_COMPONENTS.set(TEMPORAL_CATEGORY, TEMPORAL_CATEGORY_VALUES);
REGISTERED_COMPONENTS.set(ENVIRONMENTAL_CATEGORY, ENVIRONMENTAL_CATEGORY_VALUES);

export const ATTRIBUTE_SEVERITY_ORDER: VectorComponentValue[][] = [
    [S_VALUES.U, MS_VALUES.U],
    [
        CONFIDENTIALITY_IMPACT_VALUES.N,
        INTEGRITY_IMPACT_VALUES.N,
        AVAILABILITY_IMPACT_VALUES.N,
    ],
    [AV_VALUES.P, MAV_VALUES.P],
    [
        CONFIDENTIALITY_IMPACT_VALUES.L,
        INTEGRITY_IMPACT_VALUES.L,
        AVAILABILITY_IMPACT_VALUES.L,
    ],
    [PR_VALUES.H, MPR_VALUES.H],
    [AC_VALUES.H, MAC_VALUES.H],
    [
        CONFIDENTIALITY_REQUIREMENT_VALUES.L,
        INTEGRITY_REQUIREMENT_VALUES.L,
        AVAILABILITY_REQUIREMENT_VALUES.L,
    ],
    [AV_VALUES.L, MAV_VALUES.L],
    [
        CONFIDENTIALITY_IMPACT_VALUES.H,
        INTEGRITY_IMPACT_VALUES.H,
        AVAILABILITY_IMPACT_VALUES.H,
    ],
    [AV_VALUES.A, MAV_VALUES.A],
    [UI_VALUES.R, MUI_VALUES.R],
    [PR_VALUES.L, MPR_VALUES.L],
    [AC_VALUES.L, MAC_VALUES.L],
    [AV_VALUES.N, MAV_VALUES.N],
    [PR_VALUES.N, MPR_VALUES.N],
    [UI_VALUES.N, MUI_VALUES.N],
    [E_VALUES.U],
    [E_VALUES.P],
    [E_VALUES.F],
    [E_VALUES.H],
    [E_VALUES.X],
    [RC_VALUES.U],
    [RL_VALUES.O],
    [RL_VALUES.T],
    [RC_VALUES.R],
    [RL_VALUES.W],
    [AV_VALUES.X, MAV_VALUES.X],
    [AC_VALUES.X, MAC_VALUES.X],
    [PR_VALUES.X, MPR_VALUES.X],
    [UI_VALUES.X, MUI_VALUES.X],
    [S_VALUES.C, MS_VALUES.C],
    [S_VALUES.X, MS_VALUES.X],
    [CONFIDENTIALITY_IMPACT_VALUES.X],
    [INTEGRITY_IMPACT_VALUES.X],
    [AVAILABILITY_IMPACT_VALUES.X],
    [RL_VALUES.U],
    [RL_VALUES.X],
    [RC_VALUES.C],
    [RC_VALUES.X],
    [
        CONFIDENTIALITY_REQUIREMENT_VALUES.M,
        INTEGRITY_REQUIREMENT_VALUES.M,
        AVAILABILITY_REQUIREMENT_VALUES.M
    ],
    [
        CONFIDENTIALITY_REQUIREMENT_VALUES.X,
        INTEGRITY_REQUIREMENT_VALUES.X,
        AVAILABILITY_REQUIREMENT_VALUES.X
    ],
    [
        CONFIDENTIALITY_REQUIREMENT_VALUES.H,
        INTEGRITY_REQUIREMENT_VALUES.H,
        AVAILABILITY_REQUIREMENT_VALUES.H
    ],
];


export class Cvss3P1 extends CvssVector<MultiScoreResult> {

    private static readonly SCOPE_CHANGED_FACTOR = 7.52;
    private static readonly SCOPE_UNCHANGED_FACTOR = 6.42;
    private static readonly EXPLOITABILITY_COEFFICIENT = 8.22;
    private static readonly SCOPE_COEFFICIENT = 1.08;

    public constructor(initialVector?: string) {
        super(initialVector);
    }

    public getRegisteredComponents(): Map<ComponentCategory, VectorComponent<VectorComponentValue>[]> {
        return REGISTERED_COMPONENTS;
    }

    getVectorPrefix(): string {
        return "CVSS:3.1/";
    }

    getVectorName(): string {
        return "CVSS:3.1";
    }

    fillAverageVector(): void {
        this.applyVector("AV:N/AC:H/PR:N/UI:N/S:U/C:L/I:L/A:L");
    }

    fillRandomBaseVector(): void {
        this.fillRandomComponentsForCategory(BASE_CATEGORY);
    }

    fillRandomTemporalVector(): void {
        this.fillRandomComponentsForCategory(TEMPORAL_CATEGORY);
    }

    fillRandomEnvironmentalVector(): void {
        this.fillRandomComponentsForCategory(ENVIRONMENTAL_CATEGORY);
    }

    fillRandomComponentsForCategory(category: ComponentCategory): void {
        const categoryComponents = REGISTERED_COMPONENTS.get(category);
        if (!categoryComponents) {
            console.warn('Failed to pick random vector components for', category);
            return;
        }
        for (let i = 0; i < categoryComponents.length; i++) {
            const component = categoryComponents[i];
            const value = super.pickRandomDefinedComponentValue(component);
            if (value) {
                this.applyComponent(component, value);
            } else {
                console.warn('Failed to pick random vector component for', component);
                this.fillAverageVector();
                return;
            }
        }
    }

    protected calculateScoresInternal(normalize: boolean = false): MultiScoreResult {
        const baseFullyDefined = this.isBaseFullyDefined();
        const temporalAnyDefined = this.isAnyTemporalDefined();
        const environmentalAnyDefined = this.isAnyEnvironmentalDefined();
        return {
            normalized: normalize,
            base: baseFullyDefined ? super.round(this.calculateExactBaseScore(), 1) : undefined,
            impact: baseFullyDefined ? super.normalizeScore(super.round(this.calculateImpactScore(), 1), normalize ? 6.0 : 10) : undefined,
            exploitability: baseFullyDefined ? super.normalizeScore(super.round(this.calculateExactExploitabilityScore(), 1), normalize ? 3.9 : 10) : undefined,
            temporal: baseFullyDefined && temporalAnyDefined ? super.round(this.calculateExactTemporalScore(), 1) : undefined,
            environmental: baseFullyDefined && environmentalAnyDefined ? super.round(this.calculateExactEnvironmentalScore(), 1) : undefined,
            modifiedImpact: baseFullyDefined && environmentalAnyDefined ? super.normalizeScore(super.round(Math.max(0, this.calculateExactAdjustedImpactScore()), 1), normalize ? 6.1 : 10) : undefined,
            overall: super.round(this.calculateExactOverallScore(), 1),
            vector: this.toString()
        };
    }

    public calculateExactBaseScore(): number {
        if (!this.isBaseFullyDefined()) return 0;

        let impact = this.calculateExactImpactScore();
        if (impact <= 0) return 0;

        let exploitabilityScore = this.calculateExactExploitabilityScore();
        let scope = this.getComponent(S).value;

        if (!scope) {
            return super.roundUp(Math.min(impact + exploitabilityScore, 10));
        } else {
            return super.roundUp(Math.min(Cvss3P1.SCOPE_COEFFICIENT * (impact + exploitabilityScore), 10));
        }
    }

    public calculateImpactScore(): number {
        const exactImpactScore = this.calculateExactImpactScore();
        if (exactImpactScore <= 0) return 0;
        return exactImpactScore;
    }

    public calculateExactImpactScore(): number {
        let iss = this.calculateExactISSScore();
        let scope = this.getComponent(S).value;

        if (scope) {
            return Cvss3P1.SCOPE_CHANGED_FACTOR * (iss - 0.029) - 3.25 * Math.pow(iss - 0.02, 15);
        } else {
            return Cvss3P1.SCOPE_UNCHANGED_FACTOR * iss;
        }
    }

    public calculateExactISSScore(): number {
        let confidentiality = this.getComponent(C).value;
        let integrity = this.getComponent(I).value;
        let availability = this.getComponent(A).value;

        return 1 - ((1 - confidentiality) * (1 - integrity) * (1 - availability));
    }

    public calculateExactMISSScore(): number {
        let mci, mii, mai;
        let crFactor, irFactor, arFactor;

        let confidentialityImpactModified = this.getComponent(MC);
        let integrityImpactModified = this.getComponent(MI);
        let availabilityImpactModified = this.getComponent(MA);

        let confidentialityImpact = this.getComponent(C);
        let integrityImpact = this.getComponent(I);
        let availabilityImpact = this.getComponent(A);

        let confidentialityRequirement = this.getComponent(CR);
        let integrityRequirement = this.getComponent(IR);
        let availabilityRequirement = this.getComponent(AR);

        if (confidentialityImpactModified === MC.values[0]) {
            mci = confidentialityImpact.value;
        } else {
            mci = confidentialityImpactModified.value;
        }
        if (integrityImpactModified === MI.values[0]) {
            mii = integrityImpact.value;
        } else {
            mii = integrityImpactModified.value;
        }
        if (availabilityImpactModified === MA.values[0]) {
            mai = availabilityImpact.value;
        } else {
            mai = availabilityImpactModified.value;
        }

        if (confidentialityRequirement === CR.values[0]) {
            crFactor = confidentialityRequirement.value;
        } else {
            crFactor = confidentialityRequirement.value;
        }
        if (integrityRequirement === IR.values[0]) {
            irFactor = integrityRequirement.value;
        } else {
            irFactor = integrityRequirement.value;
        }
        if (availabilityRequirement === AR.values[0]) {
            arFactor = availabilityRequirement.value;
        } else {
            arFactor = availabilityRequirement.value;
        }

        return Math.min(1 - (
            (1 - crFactor * mci) *
            (1 - irFactor * mii) *
            (1 - arFactor * mai)
        ), 0.915);
    }

    public calculateExactExploitabilityScore(): number {
        const attackVector = this.getComponent(AV).value;
        const attackComplexity = this.getComponent(AC).value;
        const userInteraction = this.getComponent(UI).value;
        const scope = this.getComponent(S).value;

        let privilegesRequired;
        if (scope) {
            privilegesRequired = this.getComponent(PR).changedValue;
        } else {
            privilegesRequired = this.getComponent(PR).value;
        }

        return Cvss3P1.EXPLOITABILITY_COEFFICIENT * attackVector * attackComplexity * privilegesRequired * userInteraction;
    }

    public calculateExactTemporalScore(): number {
        if (!this.isBaseFullyDefined()) return 0;
        if (!this.isAnyTemporalDefined()) return 0;

        let exploitCodeMaturityFactor = this.getComponent(E).value;
        let remediationLevelFactor = this.getComponent(RL).value;
        let reportConfidenceFactor = this.getComponent(RC).value;
        let baseScore = this.calculateExactBaseScore();

        return super.roundUp(baseScore * exploitCodeMaturityFactor * remediationLevelFactor * reportConfidenceFactor);
    }

    public calculateExactEnvironmentalScore(): number {
        if (!this.isBaseFullyDefined()) return 0;
        if (!this.isAnyEnvironmentalDefined()) return 0;

        let modifiedImpact = this.calculateExactAdjustedImpactScore();
        if (modifiedImpact <= 0) return 0;

        let modifiedExploitability = this.calculateAdjustedExploitability();
        let exploitCodeMaturityFactor = this.getComponent(E).value;
        let remediationLevelFactor = this.getComponent(RL).value;
        let reportConfidenceFactor = this.getComponent(RC).value;

        if (this.isModifiedScope()) {
            let modifiedFactor = super.roundUp(Math.min((modifiedImpact + modifiedExploitability), 10));
            return super.roundUp(modifiedFactor * exploitCodeMaturityFactor * remediationLevelFactor * reportConfidenceFactor);
        } else {
            let modifiedFactor = super.roundUp(Math.min(Cvss3P1.SCOPE_COEFFICIENT * (modifiedImpact + modifiedExploitability), 10));
            return super.roundUp(modifiedFactor * exploitCodeMaturityFactor * remediationLevelFactor * reportConfidenceFactor);
        }
    }

    public calculateExactAdjustedImpactScore(): number {
        if (!this.isBaseFullyDefined()) return 0;
        if (!this.isAnyEnvironmentalDefined()) return 0;

        let miss = this.calculateExactMISSScore();

        if (this.isModifiedScope()) {
            return Cvss3P1.SCOPE_UNCHANGED_FACTOR * miss;
        } else {
            return Cvss3P1.SCOPE_CHANGED_FACTOR * (miss - 0.029) - 3.25 * Math.pow(miss * 0.9731 - 0.02, 13);
        }
    }

    public calculateAdjustedExploitability(): number {
        let mav = this.getFirstDefinedComponent([MAV, AV]).value;
        let mac = this.getFirstDefinedComponent([MAC, AC]).value;
        let mui = this.getFirstDefinedComponent([MUI, UI]).value;

        let mprComponent = this.getFirstDefinedComponent([MPR, PR]);
        let mpr;
        if (this.isModifiedScope()) {
            mpr = mprComponent.value;
        } else {
            mpr = mprComponent.changedValue;
        }

        return Cvss3P1.EXPLOITABILITY_COEFFICIENT * mav * mac * mpr * mui;
    }

    public isModifiedScope(): boolean {
        let scopeComponent = this.getComponent(S);
        let modifiedScopeComponent = this.getComponent(MS);

        if (modifiedScopeComponent === MS.values[0]) {
            return !scopeComponent.value;
        } else {
            return !modifiedScopeComponent.value;
        }
    }

    public calculateExactOverallScore(): number {
        if (this.isAnyEnvironmentalDefined()) return this.calculateExactEnvironmentalScore();
        else if (this.isAnyTemporalDefined()) return this.calculateExactTemporalScore();
        return this.calculateExactBaseScore();
    }


    public isBaseFullyDefined(): boolean {
        return super.isCategoryFullyDefined(BASE_CATEGORY);
    }

    public isTemporalFullyDefined(): boolean {
        return super.isCategoryFullyDefined(TEMPORAL_CATEGORY);
    }

    public isEnvironmentalFullyDefined(): boolean {
        return super.isCategoryFullyDefined(ENVIRONMENTAL_CATEGORY);
    }

    public isAnyBaseDefined(): boolean {
        return super.isCategoryPartiallyDefined(BASE_CATEGORY);
    }

    public isAnyTemporalDefined(): boolean {
        return super.isCategoryPartiallyDefined(TEMPORAL_CATEGORY);
    }

    public isAnyEnvironmentalDefined(): boolean {
        return super.isCategoryPartiallyDefined(ENVIRONMENTAL_CATEGORY);
    }

    private getJsonSchemaSeverity(score: number): SeverityType {
        if (score === 0 || isNaN(score)) {
            return "NONE";
        } else if (score <= 3.9) {
            return "LOW";
        } else if (score <= 6.9) {
            return "MEDIUM";
        } else if (score <= 8.9) {
            return "HIGH";
        } else {
            return "CRITICAL";
        }
    }

    public createJsonSchema(): JSONSchemaForCommonVulnerabilityScoringSystemVersion31 {
        const scores = this.calculateScores();
        return {
            version: "3.1",
            vectorString: this.toString(),
            baseScore: scores.base!,
            temporalScore: scores.temporal,
            environmentalScore: scores.environmental,
            baseSeverity: this.getJsonSchemaSeverity(scores.base!),
            temporalSeverity: scores.temporal ? this.getJsonSchemaSeverity(scores.temporal) : undefined,
            environmentalSeverity: scores.environmental ? this.getJsonSchemaSeverity(scores.environmental) : undefined,

            attackVector: this.getComponent(AV).jsonSchemaName as AttackVectorType,
            attackComplexity: this.getComponent(AC).jsonSchemaName as AttackComplexityType,
            privilegesRequired: this.getComponent(PR).jsonSchemaName as PrivilegesRequiredType,
            userInteraction: this.getComponent(UI).jsonSchemaName as UserInteractionType,
            scope: this.getComponent(S).jsonSchemaName as ScopeType,
            confidentialityImpact: this.getComponent(C).jsonSchemaName as CiaType,
            integrityImpact: this.getComponent(I).jsonSchemaName as CiaType,
            availabilityImpact: this.getComponent(A).jsonSchemaName as CiaType,
            exploitCodeMaturity: this.getComponent(E).jsonSchemaName as ExploitCodeMaturityType,
            remediationLevel: this.getComponent(RL).jsonSchemaName as RemediationLevelType,
            reportConfidence: this.getComponent(RC).jsonSchemaName as ConfidenceType,
            confidentialityRequirement: this.getComponent(CR).jsonSchemaName as CiaRequirementType,
            integrityRequirement: this.getComponent(IR).jsonSchemaName as CiaRequirementType,
            availabilityRequirement: this.getComponent(AR).jsonSchemaName as CiaRequirementType,
            modifiedAttackVector: this.getComponent(MAV).jsonSchemaName as ModifiedAttackVectorType,
            modifiedAttackComplexity: this.getComponent(MAC).jsonSchemaName as ModifiedAttackComplexityType,
            modifiedPrivilegesRequired: this.getComponent(MPR).jsonSchemaName as ModifiedPrivilegesRequiredType,
            modifiedUserInteraction: this.getComponent(MUI).jsonSchemaName as ModifiedUserInteractionType,
            modifiedScope: this.getComponent(MS).jsonSchemaName as ModifiedScopeType,
            modifiedConfidentialityImpact: this.getComponent(MC).jsonSchemaName as ModifiedCiaType,
            modifiedIntegrityImpact: this.getComponent(MI).jsonSchemaName as ModifiedCiaType,
            modifiedAvailabilityImpact: this.getComponent(MA).jsonSchemaName as ModifiedCiaType
        };
    }
}

export type AttackVectorType =
    | "NETWORK"
    | "ADJACENT_NETWORK"
    | "LOCAL"
    | "PHYSICAL"
export type AttackComplexityType = "HIGH" | "LOW"
export type PrivilegesRequiredType = "HIGH" | "LOW" | "NONE"
export type UserInteractionType = "NONE" | "REQUIRED"
export type ScopeType = "UNCHANGED" | "CHANGED"
export type CiaType = "NONE" | "LOW" | "HIGH"
export type ScoreType = number
export type SeverityType = "NONE" | "LOW" | "MEDIUM" | "HIGH" | "CRITICAL"
export type ExploitCodeMaturityType =
    | "UNPROVEN"
    | "PROOF_OF_CONCEPT"
    | "FUNCTIONAL"
    | "HIGH"
    | "NOT_DEFINED"
export type RemediationLevelType =
    | "OFFICIAL_FIX"
    | "TEMPORARY_FIX"
    | "WORKAROUND"
    | "UNAVAILABLE"
    | "NOT_DEFINED"
export type ConfidenceType =
    | "UNKNOWN"
    | "REASONABLE"
    | "CONFIRMED"
    | "NOT_DEFINED"
export type CiaRequirementType = "LOW" | "MEDIUM" | "HIGH" | "NOT_DEFINED"
export type ModifiedAttackVectorType =
    | "NETWORK"
    | "ADJACENT_NETWORK"
    | "LOCAL"
    | "PHYSICAL"
    | "NOT_DEFINED"
export type ModifiedAttackComplexityType = "HIGH" | "LOW" | "NOT_DEFINED"
export type ModifiedPrivilegesRequiredType =
    | "HIGH"
    | "LOW"
    | "NONE"
    | "NOT_DEFINED"
export type ModifiedUserInteractionType = "NONE" | "REQUIRED" | "NOT_DEFINED"
export type ModifiedScopeType = "UNCHANGED" | "CHANGED" | "NOT_DEFINED"
export type ModifiedCiaType = "NONE" | "LOW" | "HIGH" | "NOT_DEFINED"

export interface JSONSchemaForCommonVulnerabilityScoringSystemVersion31 {
    /**
     * CVSS Version
     */
    version: "3.1"
    vectorString: string
    attackVector?: AttackVectorType
    attackComplexity?: AttackComplexityType
    privilegesRequired?: PrivilegesRequiredType
    userInteraction?: UserInteractionType
    scope?: ScopeType
    confidentialityImpact?: CiaType
    integrityImpact?: CiaType
    availabilityImpact?: CiaType
    baseScore: ScoreType
    baseSeverity: SeverityType
    exploitCodeMaturity?: ExploitCodeMaturityType
    remediationLevel?: RemediationLevelType
    reportConfidence?: ConfidenceType
    temporalScore?: ScoreType
    temporalSeverity?: SeverityType
    confidentialityRequirement?: CiaRequirementType
    integrityRequirement?: CiaRequirementType
    availabilityRequirement?: CiaRequirementType
    modifiedAttackVector?: ModifiedAttackVectorType
    modifiedAttackComplexity?: ModifiedAttackComplexityType
    modifiedPrivilegesRequired?: ModifiedPrivilegesRequiredType
    modifiedUserInteraction?: ModifiedUserInteractionType
    modifiedScope?: ModifiedScopeType
    modifiedConfidentialityImpact?: ModifiedCiaType
    modifiedIntegrityImpact?: ModifiedCiaType
    modifiedAvailabilityImpact?: ModifiedCiaType
    environmentalScore?: ScoreType
    environmentalSeverity?: SeverityType

    [k: string]: unknown
}


export const ComponentValues = {
    CONFIDENTIALITY_IMPACT_VALUES,
    INTEGRITY_IMPACT_VALUES,
    AVAILABILITY_IMPACT_VALUES,
    CONFIDENTIALITY_REQUIREMENT_VALUES,
    AVAILABILITY_REQUIREMENT_VALUES,
    AV_VALUES,
    S_VALUES,
    AC_VALUES,
    PR_VALUES,
    UI_VALUES
}

